/** Client-side helpers to keep Thinking / answer panels clean. */

const THINKING_BLOCK_RE = /<thinking>([\s\S]*?)<\/thinking>/gi;
const THINKING_TAG_RE = /<\/?thinking>/gi;
const PROMPT_LEAK_RE =
  /(?:before using tools or recommending[\s\S]*?never skip reasoning[^\n]*\.?|for tax,\s*forms,\s*compliance[\s\S]*?do not mention or quote these instructions\.?|reason step by step inside\s*<\/?thinking>.*?tags\.?|after\s*<\/thinking>,\s*give a concise answer[^\n]*|current task domain:\s*\w+\.?)/gi;

export type StripOptions = {
  /** Trim ends only when finalizing a complete message — never mid-stream. */
  trim?: boolean;
};

/**
 * Strip thinking tags / prompt leaks from model text.
 *
 * IMPORTANT: Do not trim during incremental stream assembly. Trimming each
 * chunk drops trailing spaces (`"Let "` → `"Let"`), so the next word glues on
 * (`"Letme"`). Pass `{ trim: true }` only on final / persisted content.
 */
export function stripThinkingArtifacts(text: string, opts: StripOptions = {}): string {
  let cleaned = text
    .replace(THINKING_TAG_RE, '')
    .replace(PROMPT_LEAK_RE, '')
    .replace(/\n{3,}/g, '\n\n');
  if (opts.trim) cleaned = cleaned.trim();
  return cleaned;
}

/** Split model text into thinking + visible answer (handles unclosed tags). */
export function parseThinkingBlocks(
  text: string,
  opts: StripOptions = { trim: true }
): { thinking: string; answer: string } {
  const trim = opts.trim !== false;
  const blocks: string[] = [];
  let remaining = text.replace(THINKING_BLOCK_RE, (_m, inner: string) => {
    if (inner.trim()) blocks.push(inner.trim());
    return '';
  });

  const openIdx = remaining.toLowerCase().indexOf('<thinking>');
  const closeIdx = remaining.toLowerCase().indexOf('</thinking>');
  if (openIdx !== -1 && closeIdx === -1) {
    const dangling = remaining.slice(openIdx + '<thinking>'.length);
    if (dangling.trim()) blocks.push(dangling.trim());
    remaining = remaining.slice(0, openIdx);
  }

  return {
    thinking: stripThinkingArtifacts(blocks.join('\n\n'), { trim }),
    answer: stripThinkingArtifacts(remaining, { trim }),
  };
}
